/**
 * Phase 19 / Task 19.4 (DEC-325) — the Finance dashboard's two Recharts charts.
 *
 * - Revenue by job type: a pie of invoiced dollars (every VIEW_INVOICES role sees them,
 *   DEC-332).
 * - Invoiced volume: BBL on invoiced jobs per company-local day.
 *
 * Kept in their own module so page tests can mock them (jsdom has no layout, so a
 * ResponsiveContainer renders nothing there).
 */

import * as React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { JobTypeRevenue, VolumePoint } from './use-finance-dashboard';
import { formatBbl, formatCents, formatShortDay, plural } from './finance-format';

/** Distinct, dark-theme-safe slice colours; the legend table names each one. */
export const SLICE_COLORS = [
  '#7b96e8',
  '#6fcf8e',
  '#f6b65e',
  '#c49bf0',
  '#f38b8b',
  '#5fc4c0',
  '#e8d27a',
  '#a9adb8',
] as const;

const tooltipStyle: React.CSSProperties = {
  background: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  fontSize: '12px',
  color: 'var(--color-text-primary)',
};

export function JobTypePie({ rows }: { rows: readonly JobTypeRevenue[] }): React.JSX.Element {
  const data = rows.map((r) => ({
    name: r.label,
    value: r.cents,
    label: `${formatCents(r.cents)} · ${plural(r.jobs, 'job')}`,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
          {data.map((d, i) => (
            <Cell key={d.name} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(_v, _n, item: { payload?: { label?: string } }) => item.payload?.label ?? ''}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function InvoicedVolumeChart({
  points,
}: {
  points: readonly VolumePoint[];
}): React.JSX.Element {
  const data = points.map((p) => ({
    day: formatShortDay(p.day),
    bbl: Number(p.bbl),
    label: `${formatBbl(p.bbl)} BBL · ${plural(p.loads, 'load')}`,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="var(--color-border-secondary)" vertical={false} />
        <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--color-text-tertiary)' }} />
        <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-tertiary)' }} width={56} />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(_v, _n, item: { payload?: { label?: string } }) => [
            item.payload?.label ?? '',
            'Invoiced',
          ]}
        />
        <Bar dataKey="bbl" fill="var(--color-accent-blue)" />
      </BarChart>
    </ResponsiveContainer>
  );
}
